<template>
  <div class="grid grid-cols-2 gap-4">
    <!-- Interview Stage -->
    <JobApplicationStatusDropdown
      v-model="interviewStatus"
      status-type="interview"
      :icon="Loader"
      label="Interview Stage"
      :show-label="true"
      :show-comment="true"
      :editable="!isCurrentUserReferrer"
      :disabled="disabled || isCurrentUserReferrer"
      :loading="loadingStates.interview_status"
      @update:model-value="handleInterviewStatusChange"
    />

    <!-- Application Status -->
    <JobApplicationStatusDropdown
      v-model="applicationStatus"
      status-type="application"
      :icon="Loader"
      label="Application Status"
      :show-label="true"
      :show-comment="true"
      :editable="!isCurrentUserReferrer"
      :disabled="disabled || isCurrentUserReferrer"
      :loading="loadingStates.application_status"
      @update:model-value="handleApplicationStatusChange"
    />

    <!-- Referral Type — hidden for limited users -->
    <JobApplicationStatusDropdown
      v-if="!hideReferralFields"
      v-model="referralType"
      status-type="referralType"
      :icon="Handshake"
      label="Referral Type"
      :show-label="true"
      :show-comment="true"
      :editable="!isCurrentUserReferrer"
      :disabled="disabled || isCurrentUserReferrer"
      :loading="loadingStates.referral_type"
      @update:model-value="handleReferralTypeChange"
    />

    <!-- Referral Status — hidden for limited users -->
    <JobApplicationStatusDropdown
      v-if="!hideReferralFields"
      v-model="referralStatus"
      status-type="referralStatus"
      :filtered-options="filteredReferralStatusOptions"
      :icon="Loader"
      label="Referral Status"
      :show-label="true"
      :show-comment="true"
      :editable="isGuide || isCurrentUserReferrer || !isReferralStatusDeadend"
      :disabled="(disabled && !isCurrentUserReferrer) || isDeadEndApplicationStatus || ((noReferrerAvailable || tierUnavailable) && !isGuide && !isCurrentUserReferrer)"
      :loading="loadingStates.referral_status"
      @update:model-value="handleReferralStatusChange"
      :selected-application="selectedApplication"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useApplicationsStore } from '@/stores/applications'
import { useMyReferralsStore } from '@/stores/myReferrals'
import JobApplicationStatusDropdown from '@/components/common/jobApplication/JobApplicationStatusDropdown.vue'
import type {
  ApplicationStatus,
  InterviewStatus,
  ReferralType,
  ReferralStatus
} from '@/types/application'
import type { ReferrerAllowedStatus } from '@/types/referrerDashboard'
import { referralStatusOptions, referrerEditableStatusOptions, referrerEditableStatusValues } from '@/const/applicationStatuses'
import { Loader, Handshake } from 'lucide-vue-next'
import { toast } from '@/components/ui/toast/use-toast'
import { useOfferNegotiationModal } from '@/composables/useOfferNegotiationModal'

const props = defineProps<{
  selectedApplication: any
  applicationId: string
  disabled?: boolean
  isCurrentUserReferrer?: boolean
  hideReferralFields?: boolean
}>()

const emit = defineEmits<{
  'statusUpdated': []
  'refreshRequested': []
}>()

const { isGuide } = useAuth()
const applicationsStore = useApplicationsStore()
const myReferralsStore = useMyReferralsStore()
const { showModal: showOfferNegotiationModal } = useOfferNegotiationModal()

// Computed properties for application statuses
const applicationStatuses = computed({
  get: () => ({
    application_status: props.selectedApplication?.application_status ?? 'NOT_STARTED',
    interview_status: props.selectedApplication?.interview_status ?? 'NOT_STARTED',
    referral_type: props.selectedApplication?.referral_type ?? 'NO_REFERRAL',
    referral_status: props.selectedApplication?.referral_status ?? 'NOT_STARTED'
  }),
  set: () => {
    // Updates are handled through the API calls in handleStatusUpdate
  }
})

const isDeadEndApplicationStatus = computed(() => {
  return ['POSITION_CLOSED', 'REJECTED', 'CANCELLED', 'OFFER_SIGNED'].includes(props.selectedApplication?.application_status)
})

const noReferrerAvailable = computed(() => {
  return !props.selectedApplication?.referrers_exist_for_employer
})

const tierUnavailable = computed(() => props.selectedApplication?.referral_tier_allowed === false)

// Check if Match Assessment has passed successfully
const isMatchAssessmentSuccessful = computed(() => {
  return props.selectedApplication?.match_status === 'COMPLETED' && 
         props.selectedApplication?.match_score !== null && 
         props.selectedApplication?.match_score !== undefined
})

// Local state
const applicationStatus = ref(applicationStatuses.value.application_status)
const interviewStatus = ref(applicationStatuses.value.interview_status)
const referralType = ref(applicationStatuses.value.referral_type)
const referralStatus = ref(applicationStatuses.value.referral_status)

// Loading states for each status field
type StatusField = 'application_status' | 'interview_status' | 'referral_type' | 'referral_status'

const loadingStates = ref<Record<StatusField, boolean>>({
  application_status: false,
  interview_status: false,
  referral_type: false,
  referral_status: false
})

// Computed properties for referral status handling
const isReferralStatusDeadend = computed(() => {
  const currentStatus = referralStatusOptions.find((opt) => opt.value === referralStatus.value)
  return currentStatus?.deadend === true
})

const filteredReferralStatusOptions = computed(() => {
  if (isGuide.value) {
    return referralStatusOptions
  }
  if (props.isCurrentUserReferrer) {
    return referrerEditableStatusOptions
  }
  return referralStatusOptions.filter((opt) => !opt.internal)
})

// Watch for external changes - only watch specific status fields to avoid unnecessary updates
watch(
  () => ({
    application_status: props.selectedApplication?.application_status,
    interview_status: props.selectedApplication?.interview_status,
    referral_type: props.selectedApplication?.referral_type,
    referral_status: props.selectedApplication?.referral_status
  }),
  (newStatuses) => {
    applicationStatus.value = newStatuses.application_status ?? 'NOT_STARTED'
    interviewStatus.value = newStatuses.interview_status ?? 'NOT_STARTED'
    referralType.value = newStatuses.referral_type ?? 'NO_REFERRAL'
    referralStatus.value = newStatuses.referral_status ?? 'NOT_STARTED'
  },
  { immediate: true }
)


// Handle status update for a single field
const handleStatusUpdate = async (field: StatusField, value: string, previousValue: string): Promise<boolean> => {
  loadingStates.value[field] = true
  try {
    await applicationsStore.updateApplication(props.applicationId, {
      [field]: value
    }, true)
    
    // Show success toast
    toast({
      title: "Status updated",
      description: "Application status has been successfully updated.",
    })
    
    emit('statusUpdated')
    emit('refreshRequested')
    return true
  } catch (error: any) {
    console.error(`Failed to update ${field}:`, error)
    
    // Extract error message from response if available
    let errorMessage = "Failed to update application status. Please try again."
    
    if (error?.response?.data) {
      const responseData = error.response.data
      // Check if response has validation errors in the format: {"field_name": ["error message"]}
      for (const key in responseData) {
        if (Array.isArray(responseData[key]) && responseData[key].length > 0) {
          // Join multiple error messages with a comma if there are multiple
          errorMessage = responseData[key].join(', ')
          break
        }
      }
    }
    
    // Show error toast
    toast({
      title: "Update failed",
      description: errorMessage,
      variant: "destructive",
      duration: 10000,
    })
    
    // On error, revert the local change back to the original value
    switch (field) {
      case 'application_status':
        applicationStatus.value = previousValue as ApplicationStatus
        break
      case 'interview_status':
        interviewStatus.value = previousValue as InterviewStatus
        break
      case 'referral_type':
        referralType.value = previousValue as ReferralType
        break
      case 'referral_status':
        referralStatus.value = previousValue as ReferralStatus
        break
    }
    return false
  } finally {
    loadingStates.value[field] = false
  }
}

// Handle status changes
const handleApplicationStatusChange = (value: any) => {
  if (typeof value !== 'string') return
  
  const previousValue = applicationStatus.value
  // Update local ref immediately for instant UI feedback
  applicationStatus.value = value as ApplicationStatus
  
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  handleStatusUpdate('application_status', value, previousValue)
}

const handleInterviewStatusChange = async (value: any) => {
  if (typeof value !== 'string') return
  
  const previousValue = interviewStatus.value
  // Update local ref immediately for instant UI feedback
  interviewStatus.value = value as InterviewStatus
  
  const success = await handleStatusUpdate('interview_status', value, previousValue)
  if (success && value === 'OFFER_NEGOTIATION_STAGE') {
    showOfferNegotiationModal(props.applicationId)
  }
}

const handleReferralTypeChange = (value: any) => {
  if (typeof value !== 'string') return
  
  const previousValue = referralType.value
  // Update local ref immediately for instant UI feedback
  referralType.value = value as ReferralType
  
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  handleStatusUpdate('referral_type', value, previousValue)
}

const handleReferralStatusChange = async (value: any) => {
  if (typeof value !== 'string') return

  if (value === 'READY_FOR_REFERRAL' && !props.selectedApplication?.resume_file) {
    toast({
      title: 'Resume required',
      description: 'Please select an approved resume for this application before marking it ready for referral.',
      variant: 'destructive',
    })
    return
  }

  const previousValue = referralStatus.value
  referralStatus.value = value as ReferralStatus

  if (props.isCurrentUserReferrer) {
    if (!referrerEditableStatusValues.includes(value as ReferrerAllowedStatus)) {
      referralStatus.value = previousValue as ReferralStatus
      return
    }

    loadingStates.value.referral_status = true
    try {
      await myReferralsStore.updateReferralStatus(props.applicationId, value as ReferrerAllowedStatus)
      if (applicationsStore.selectedApplication) {
        applicationsStore.selectedApplication.referral_status = value as ReferralStatus
      }
      toast({
        title: 'Referral status updated',
        description: 'Your referral status has been updated successfully.',
      })
      emit('statusUpdated')
      emit('refreshRequested')
    } catch {
      referralStatus.value = previousValue as ReferralStatus
      toast({
        title: 'Update failed',
        description: 'Unable to update referral status. Please try again.',
        variant: 'destructive',
        duration: 10000,
      })
    } finally {
      loadingStates.value.referral_status = false
    }
    return
  }

  // Optimistically update match_status if we are requesting a referral
  if (value === 'READY_FOR_REFERRAL' && applicationsStore.selectedApplication) {
    applicationsStore.selectedApplication.match_status = 'PENDING'
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  handleStatusUpdate('referral_status', value, previousValue)
}
</script>
